/**
 * Define a secret field, as a panel form. The key is what capability
 * configurations reference as `{"$secret": "<key>"}`; each organization, and
 * the platform as a fallback, then fills in its own value.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import { createSecretField } from '@optum/api-client/endpoints/secret-fields';
import type { SecretFieldType } from '@optum/api-client/types';

import {
  PanelFormActions,
  PanelFormFields,
  usePanelFormDone,
} from '../../components/shell/PanelForm.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { Field } from '../../ui/Field.js';
import { Input } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';

const TYPES: readonly SecretFieldType[] = ['string', 'integer', 'number', 'boolean'];

export function NewSecretFieldForm() {
  const qc = useQueryClient();
  const toast = useToast();
  const done = usePanelFormDone();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<SecretFieldType>('string');
  const [isSecret, setIsSecret] = useState(true);

  const create = useMutation({
    mutationFn: () =>
      createSecretField({
        key,
        label,
        description: description || null,
        type,
        is_secret: isSecret,
      }),
    onSuccess: () => {
      toast.success(`Created ${key}`);
      qc.invalidateQueries({ queryKey: qk.secretFields() });
      qc.invalidateQueries({ queryKey: qk.globalSecrets() });
      done();
    },
    onError: (e) => toast.error('Create failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <PanelFormFields onSubmit={() => create.mutate()}>
      <Field id="sf-key" label="Key" required hint="lowercase, snake_case (e.g. openai_api_key)">
        <Input
          id="sf-key"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          pattern="[a-z][a-z0-9_]*"
          required
          autoFocus
        />
      </Field>
      <Field id="sf-label" label="Label" required>
        <Input id="sf-label" value={label} onChange={(e) => setLabel(e.target.value)} required />
      </Field>
      <Field id="sf-desc" label="Description">
        <Input id="sf-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field id="sf-type" label="Type" required>
        <Select
          id="sf-type"
          value={type}
          onChange={(value) => setType(value as SecretFieldType)}
          options={TYPES.map((t) => ({ value: t, label: t }))}
        />
      </Field>
      <Checkbox
        checked={isSecret}
        onChange={(checked) => setIsSecret(checked)}
        label="Treat as secret (mask in UI)"
      />
      <PanelFormActions
        submitLabel="Create field"
        pending={create.isPending}
        disabled={!key || !label}
        dirty={key !== '' || label !== '' || description !== ''}
      />
    </PanelFormFields>
  );
}
